import React, {
  useMemo, useState, useCallback, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Divider,
  Popover,
  Table,
  Row,
  Col,
} from 'antd';
import PropTypes from 'prop-types';

import OnTraccrButton from '../../../common/buttons/OnTraccrButton';

import CompanySettingsCard from '../../CompanySettingsCard';

import SageIntacctIntegration from './SageIntacctIntegration';
import SageIntacctIntegrationDrawer from './SageIntacctIntegrationDrawer';

import { getSageIntacctIntegrations } from '../../state/settings.actions';
import { getStatuses as getEquipmentStatuses } from '../../../equipment/state/equipment.actions';

import ErrorRecipientSelector from '../ErrorRecipientSelector';

const onHeaderCell = () => ({
  style: {
    backgroundColor: 'white',
  },
});

function SageIntacct({
  visible,
}) {
  const dispatch = useDispatch();

  const divisions = useSelector((state) => state.settings.divisions);
  const integrations = useSelector((state) => state.settings.sageIntacctIntegrations);
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState();

  const closeAddDrawer = useCallback(() => {
    setAddDrawerOpen(false);
    setSelectedIntegration();
  }, []);
  const openAddDrawer = useCallback(() => setAddDrawerOpen(true), []);
  const onRowClick = useCallback((record) => {
    openAddDrawer();
    setSelectedIntegration(record);
  }, [openAddDrawer]);

  useEffect(() => {
    if (visible) {
      dispatch(getSageIntacctIntegrations());
      dispatch(getEquipmentStatuses());
    }
  }, [visible]);

  const columns = useMemo(() => [{
    title: 'Divisions',
    dataIndex: 'divisions',
    onHeaderCell,
    render: (divisionIds) => {
      const {
        names,
        excessCount,
        allNames = [],
      } = divisionIds?.reduce((acc, divId, index) => {
        const { [divId]: { name } = {} } = divisions;
        if (!name) return acc;
        acc.allNames.push(<p key={divId}>{name}</p>);
        if (index > 2) {
          acc.excessCount += 1;
        } else {
          acc.names += `${index === 0 ? '' : ', '}${name}`;
        }
        return acc;
      }, { names: '', excessCount: 0, allNames: [] }) ?? {};
      if (excessCount === 0) return names;
      const suffix = excessCount === 1 ? '' : 's';
      const fullName = excessCount === 0
        ? names
        : `${names} and ${excessCount} other division${suffix}`;
      return (
        <Popover
          content={(
            <div>
              <span style={{ fontWeight: 'bold', fontSize: 18, color: 'black' }}>Divisions</span>
              <Divider style={{ margin: 0, marginBottom: 5 }} />
              {allNames}
            </div>
          )}
        >
          {fullName}
        </Popover>
      );
    },
  }], [divisions]);

  const isSingleDiv = useMemo(() => Object.keys(divisions).length <= 1, [divisions]);
  return (
    <>
      <CompanySettingsCard
        title="Sage Intacct"
        leftHeaderView={(
          <Row justify="end" gutter={10}>
            <Col>
              {
                isSingleDiv
                  ? null
                  : (
                    <OnTraccrButton
                      title="Add"
                      type="primary"
                      onClick={openAddDrawer}
                    />
                  )
              }
            </Col>
          </Row>
      )}
      >
        {
          isSingleDiv
            ? (
              <SageIntacctIntegration
                selectedIntegration={integrations?.[0]}
                isSingleDiv
              />
            ) : (
              <Table
                columns={columns}
                dataSource={integrations}
                pagination={false}
                size="small"
                scroll={{ y: 'calc(100vh - 220px', x: 'hidden' }}
                onRow={(record) => ({
                  onClick: () => onRowClick(record),
                })}
                rowClassName="sage-intacct-integration-row"
              />
            )
        }
      </CompanySettingsCard>
      <CompanySettingsCard title="Settings">
        <ErrorRecipientSelector
          recipientsKey="sageIntacctErrorRecipients"
          integration="Sage Intacct"
        />
      </CompanySettingsCard>
      <SageIntacctIntegrationDrawer
        visible={addDrawerOpen}
        onClose={closeAddDrawer}
        selectedIntegration={selectedIntegration}
      />
    </>
  );
}

SageIntacct.propTypes = {
  visible: PropTypes.bool.isRequired,
};

export default SageIntacct;
